
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Staff } from "@/types/staff";
import { useToast } from "@/hooks/use-toast";
import { useDebounce } from "@/hooks/use-debounce";

export const useStaff = () => {
  const [staff, setStaff] = useState<Staff[]>([]);
  const [loading, setLoading] = useState(true);
  const [staffToDelete, setStaffToDelete] = useState<Staff | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 10;
  const { toast } = useToast();
  
  const debouncedSearch = useDebounce(searchQuery, 500);

  const fetchStaff = async (search: string = "", currentPage: number = 1) => {
    try {
      setLoading(true);
      let query = supabase
        .from('staff')
        .select('*', { count: 'exact' })
        .order('created_at', { ascending: false });

      // Add search conditions if search string exists
      if (search) {
        query = query
          .or(`email.ilike.%${search}%,mobile_number.ilike.%${search}%,school_id.ilike.%${search}%`);
      }

      // Add pagination
      query = query.range((currentPage - 1) * pageSize, currentPage * pageSize - 1);

      const { data, error, count } = await query;

      if (error) throw error;

      setStaff(data || []);
      setTotalCount(count || 0);
    } catch (error: any) {
      console.error('Error fetching staff:', error);
      toast({
        title: "Error",
        description: "Failed to fetch staff",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    fetchStaff(searchQuery, newPage);
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    setPage(1); // Reset to first page when search changes
  };

  const handleDelete = async (staff: Staff) => {
    try {
      const { error } = await supabase
        .from("staff")
        .delete()
        .eq("id", staff.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Staff deleted successfully",
      });

      fetchStaff(searchQuery, page);
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to delete staff",
        variant: "destructive",
      });
    } finally {
      setStaffToDelete(null);
    }
  };

  useEffect(() => {
    fetchStaff(debouncedSearch, page);
  }, [debouncedSearch]);

  return {
    staff,
    loading,
    staffToDelete,
    setStaffToDelete,
    handleDelete,
    fetchStaff,
    searchQuery,
    handleSearchChange,
    page,
    pageSize,
    totalCount,
    handlePageChange,
  };
};
